import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaWallet, FaArrowDown, FaTimes, FaExclamationCircle,
  FaCheckCircle, FaSync, FaUniversity, FaRupeeSign
} from "react-icons/fa";

const ReporterWallet = () => {
  const [walletData, setWalletData] = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    accountName: "", accountNumber: "", ifsc: "", bankName: "", amount: "",
  });
  const [withdrawError, setWithdrawError] = useState("");
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    accountName: "", accountNumber: "", ifsc: "", bankName: "", amount: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;

  const fetchWalletData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/get/wallet/detail`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setWalletData({
        balance: response.data.balance || 0,
        transactions: response.data.transactions || [],
      });
    } catch (err) {
      setError("Failed to load wallet data");
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError(null);
      await fetchWalletData();
      setLoading(false);
    };
    load();
  }, []);

  const handleWithdrawChange = (e) => {
    const { name, value } = e.target;
    let isValid = true, errorMessage = "";
    switch (name) {
      case "accountName":
        if (value && !/^[A-Za-z\s]*$/.test(value)) { isValid = false; errorMessage = "Alphabets only"; }
        break;
      case "accountNumber":
        if (value && !/^\d*$/.test(value)) { isValid = false; errorMessage = "Digits only"; }
        else if (value && value.length > 18) { isValid = false; errorMessage = "Max 18 digits"; }
        else if (value && value.length > 0 && value.length < 9) errorMessage = "Min 9 digits";
        break;
      case "ifsc":
        if (value && value.length > 11) { isValid = false; errorMessage = "Max 11 characters"; }
        else if (value && value.length === 11 && !/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(value)) { isValid = false; errorMessage = "Invalid IFSC format"; }
        break;
      case "bankName":
        if (value && !/^[A-Za-z\s]*$/.test(value)) { isValid = false; errorMessage = "Alphabets only"; }
        break;
      case "amount":
        const num = parseFloat(value);
        if (value && (isNaN(num) || num <= 0)) { isValid = false; errorMessage = "Enter valid amount"; }
        else if (value && num > 100000) { isValid = false; errorMessage = "Max ₹1,00,000"; }
        break;
      default: break;
    }
    if (isValid || value === "") setWithdrawForm({ ...withdrawForm, [name]: value });
    setValidationErrors({ ...validationErrors, [name]: isValid || value === "" ? "" : errorMessage });
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    setWithdrawError("");
    if (Object.values(validationErrors).some(e => e)) { setWithdrawError("Fix all errors first."); return; }
    if (!withdrawForm.accountName || !withdrawForm.accountNumber || !withdrawForm.ifsc || !withdrawForm.bankName || !withdrawForm.amount) {
      setWithdrawError("All fields are required."); return;
    }
    if (withdrawForm.accountNumber.length < 9 || withdrawForm.accountNumber.length > 18) { setWithdrawError("Account number must be 9–18 digits."); return; }
    if (!/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(withdrawForm.ifsc)) { setWithdrawError("Invalid IFSC code."); return; }
    const amount = parseFloat(withdrawForm.amount);
    if (amount <= 0 || amount > 100000) { setWithdrawError("Amount must be ₹1 – ₹1,00,000."); return; }
    setWithdrawLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/wallet/withdraw`,
        { accountHolder: withdrawForm.accountName, accountNumber: withdrawForm.accountNumber, ifscCode: withdrawForm.ifsc, bankName: withdrawForm.bankName, amount: withdrawForm.amount },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" } }
      );
      if (res.data.success) toast.success("Withdrawal request submitted!");
      setShowModal(false);
      setWithdrawForm({ accountName: "", accountNumber: "", ifsc: "", bankName: "", amount: "" });
      fetchWalletData();
    } catch (err) {
      setWithdrawError(err.response?.data?.message || "Submission failed. Try again.");
    }
    setWithdrawLoading(false);
  };

  const paginatedTransactions = [...walletData.transactions].reverse().slice(
    (currentPage - 1) * rowsPerPage, currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(walletData.transactions.length / rowsPerPage);

  const getStatusStyle = (status) => {
    if (!status) return "bg-slate-100 text-slate-600";
    const s = status.toLowerCase();
    if (s === "completed" || s === "credit") return "bg-green-100 text-green-700";
    if (s === "pending") return "bg-amber-100 text-amber-700";
    if (s === "failed" || s === "debit") return "bg-red-100 text-red-600";
    return "bg-slate-100 text-slate-600";
  };

  const isDebit = (t) => t.type?.toLowerCase() === "debit" || t.status?.toLowerCase() === "debit";

  const inputCls = (field) =>
    `w-full text-sm px-3 py-2.5 border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition ${validationErrors[field] ? "border-red-400 bg-red-50" : "border-slate-200"}`;

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
        <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Loading Wallet...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Hero Balance Card ─────────────────────────── */}
      <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 px-4 pt-6 pb-20">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Reporter Wallet</p>
              <h1 className="text-base font-black text-white">My Balance</h1>
            </div>
            <button
              onClick={() => { setLoading(true); fetchWalletData().finally(() => setLoading(false)); }}
              className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <FaSync className="w-3.5 h-3.5 text-white/70" />
            </button>
          </div>

          {/* Balance Pill */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-5 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/30 flex items-center justify-center">
                <FaWallet className="w-4 h-4 text-indigo-300" />
              </div>
              <div>
                <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Available Balance</p>
                <p className="text-2xl font-black text-white">₹{Number(walletData.balance).toFixed(2)}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowModal(true)}
                className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black py-2.5 rounded-xl transition-colors"
              >
                <FaArrowDown className="w-3 h-3" /> Withdraw
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 border border-white/10 rounded-xl p-3 text-center">
              <p className="text-lg font-black text-green-400">
                ₹{walletData.transactions.filter(t => !isDebit(t)).reduce((s, t) => s + Math.abs(t.amount || 0), 0).toFixed(2)}
              </p>
              <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mt-0.5">Total Credits</p>
            </div>
            <div className="bg-white/10 border border-white/10 rounded-xl p-3 text-center">
              <p className="text-lg font-black text-red-400">
                ₹{walletData.transactions.filter(t => isDebit(t)).reduce((s, t) => s + Math.abs(t.amount || 0), 0).toFixed(2)}
              </p>
              <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mt-0.5">Total Debits</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Transaction List ──────────────────────────── */}
      <div className="max-w-lg mx-auto px-3 -mt-10 pb-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div>
              <h2 className="text-sm font-black text-slate-900">Transaction History</h2>
              <p className="text-[10px] text-slate-400 font-bold">{walletData.transactions.length} total transactions</p>
            </div>
            <span className="text-[9px] font-black text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase tracking-widest">
              Page {currentPage}/{totalPages || 1}
            </span>
          </div>

          {paginatedTransactions.length === 0 ? (
            <div className="py-16 flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-2">
                <FaWallet className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm font-black text-slate-900">No transactions yet</p>
              <p className="text-xs text-slate-400">Your wallet activity will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {paginatedTransactions.map((txn, idx) => (
                <div key={idx} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50/70 transition-colors">
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isDebit(txn) ? "bg-red-100" : "bg-green-100"}`}>
                    <FaRupeeSign className={`w-3.5 h-3.5 ${isDebit(txn) ? "text-red-500" : "text-green-600"}`} />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-900 truncate capitalize">{txn.description || txn.type || "Transaction"}</p>
                    <p className="text-[9px] text-slate-400 font-bold mt-0.5 truncate">
                      {new Date(txn.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      {" · "}
                      {new Date(txn.date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
                    </p>
                    <span className={`inline-block text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full mt-1 ${getStatusStyle(txn.status)}`}>
                      {txn.status || txn.type}
                    </span>
                  </div>

                  {/* Amount */}
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-black ${isDebit(txn) ? "text-red-600" : "text-green-600"}`}>
                      {isDebit(txn) ? "-" : "+"}₹{Math.abs(txn.amount || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between bg-slate-50/60">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="text-xs font-black text-indigo-600 disabled:text-slate-300 uppercase tracking-widest transition-colors"
              >← Prev</button>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="text-xs font-black text-indigo-600 disabled:text-slate-300 uppercase tracking-widest transition-colors"
              >Next →</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Withdrawal Modal ──────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm px-0 sm:px-4">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-3xl z-10">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <FaUniversity className="w-3.5 h-3.5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900">Withdrawal Request</h3>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Max ₹1,00,000</p>
                </div>
              </div>
              <button
                onClick={() => { setShowModal(false); setWithdrawError(""); }}
                className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <FaTimes className="w-3.5 h-3.5 text-slate-600" />
              </button>
            </div>

            <form onSubmit={handleWithdrawSubmit} className="px-5 py-4 space-y-4">
              {[
                { name: "accountName", label: "Account Holder Name", placeholder: "Full name (alphabets only)", type: "text" },
                { name: "accountNumber", label: "Account Number", placeholder: "9–18 digit account number", type: "text", maxLength: 18 },
                { name: "ifsc", label: "IFSC Code", placeholder: "e.g. SBIN0123456", type: "text", maxLength: 11, upper: true },
                { name: "bankName", label: "Bank Name", placeholder: "Bank name (alphabets only)", type: "text" },
                { name: "amount", label: "Amount (₹)", placeholder: "Enter amount (max ₹1,00,000)", type: "number" },
              ].map(field => (
                <div key={field.name}>
                  <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    name={field.name}
                    value={withdrawForm[field.name]}
                    onChange={handleWithdrawChange}
                    className={inputCls(field.name)}
                    placeholder={field.placeholder}
                    maxLength={field.maxLength}
                    style={field.upper ? { textTransform: "uppercase" } : {}}
                    required
                  />
                  {validationErrors[field.name] && (
                    <p className="mt-1 text-[10px] font-bold text-red-500 flex items-center gap-1">
                      <FaExclamationCircle className="w-3 h-3" /> {validationErrors[field.name]}
                    </p>
                  )}
                </div>
              ))}

              {withdrawError && (
                <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                  <FaExclamationCircle className="w-3.5 h-3.5 flex-shrink-0" /> {withdrawError}
                </div>
              )}

              <button
                type="submit"
                disabled={withdrawLoading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl text-sm font-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {withdrawLoading
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Submitting...</>
                  : <><FaCheckCircle className="w-4 h-4" /> Submit Request</>
                }
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReporterWallet;
